import { HttpService } from "../services/HttpService";
import type React from "react";
export interface RoleDto {
  id: string;
  internal_role: string;
  description?: string;
  created_at?: Date;
  updated_at?: Date;
}
export interface MenuConfigResponseDto {
  success: boolean;
  data: Record<string, MenuItem[]>;
}
export interface MenuItem {
  id: string;
  name: string;
  icon:
    | string
    | React.ComponentType<{
        size?: number | string;
        color?: string;
        className?: string;
        style?: React.CSSProperties;
      }>;
  path: string;
  active: boolean;
  readOnly?: boolean;
  type?: "group";
  children?: MenuItem[];
}
export interface IMenuConfigQuery {
  roles?: string;
}
export interface IRoleRepository {
  getAllRoles(): Promise<RoleDto[]>;
  getRoleById(id: string): Promise<RoleDto | null>;
  getMenuConfigurations(
    query?: IMenuConfigQuery,
  ): Promise<MenuConfigResponseDto>;
}
export class RoleRepository implements IRoleRepository {
  private readonly httpService: HttpService;
  constructor() {
    this.httpService = new HttpService();
  }
  /**
   * Fetch all roles
   * GET /roles/getAll
   */
  async getAllRoles(): Promise<RoleDto[]> {
    const response = await this.httpService.get<RoleDto[]>("/roles/getAll");
    return response.data || [];
  }
  /**
   * Fetch a single role by ID
   * GET /roles/get/:id
   */
  async getRoleById(id: string): Promise<RoleDto | null> {
    const response = await this.httpService.get<RoleDto>(`/roles/get/${id}`);
    return response.data || null;
  }
  /**
   * Fetch menu configurations for roles
   * GET /roles/menu-config?roles=role1,role2
   *
   * @param query Optional query parameters including roles filter (comma-separated)
   */
  async getMenuConfigurations(
    query?: IMenuConfigQuery,
  ): Promise<MenuConfigResponseDto> {
    try {
      const params = new URLSearchParams();
      if (query?.roles) {
        params.append("roles", query.roles);
      }
      const url = params.toString()
        ? `/roles/menu-config?${params.toString()}`
        : "/roles/menu-config";
      const response =
        await this.httpService.get<Record<string, MenuItem[]>>(url);
      // Check if response has data
      const menuData = response.data || {};
      if (Object.keys(menuData).length === 0) {
        throw new Error("API returned empty menu configuration");
      }
      return {
        success: true,
        data: menuData,
      };
    } catch {
      // Return empty data instead of throwing, so fallback can work
      return {
        success: false,
        data: {},
      };
    }
  }
}
